import React, { type FC, useContext, type PropsWithChildren } from 'react';

import { withURL } from '@lucid/utils';
import { withImage, useSmartlingStrings } from '@lucid/hooks';

import Box from '../box/box';
import Heading from '../heading/heading';
import Link from '../link/link';
import Text from '../text/text';
import Paper from '../paper/paper';
import LayoutContext from '../context/layout-context';
import Image from '../image/image';
import type { Lucid } from '../../types/types';

export interface InTextCtaProps {
  /** The collection block from Directus */
  item: Lucid.Directus.Generated.Blog_Inline_Cta;
}

const InTextCta: FC<PropsWithChildren<InTextCtaProps>> = ({ item }) => {
  const { shortCode, urlLangPrefix, product } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_read_more'],
    shortCode,
  });

  const hasEntity = item.heading || item.category || item.image || item.link || item.text;
  if (!hasEntity) return null;

  return (
    <Paper
      key={item.id}
      css={theme => ({
        label: 'blogInTextCTA',
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        maxWidth: '480px',
        margin: `${theme.space.s80}px auto`,
        padding: theme.space.s16,
        [theme.breakpoints.bp900.min]: {
          padding: theme.space.s24,
        },
      })}
      variant="raisedLow"
    >
      {item?.image && item?.image?.filename_disk && (
        <Image
          css={{
            width: '100px',
            height: '100px',
            flex: '0 1 100px',
          }}
          imageAlt={item.image.translations?.[0]?.alt || ''}
          imageSrc={withImage(item.image)}
          loading="lazy"
        />
      )}
      <Box
        css={theme => ({
          flex: '1 1 100%',
          marginLeft: 0,
          marginTop: theme.space.s16,
          [theme.breakpoints.bp900.min]: {
            flex: '1 1',
            marginLeft: theme.space.s16,
            marginTop: 0,
          },
        })}
      >
        {item.category && (
          <Heading as="h4" id={`cta-category-${item.id}`} variant="headingS">
            {item.category}
          </Heading>
        )}
        {item.heading && (
          <Heading as="h4" id={`cta-heading-${item.id}`} variant="headingS">
            {item.heading}
          </Heading>
        )}
        {item.text && (
          <Text id={`cta-text-${item.id}`} variant="paragraphS400">
            {item.text}
          </Text>
        )}
        {item.link && item.link?.final_url && (
          <Link
            aria-describedby={
              item.heading ? `cta-heading-${item.id}` : item.text ? `cta-text-${item.id}` : `cta-category-${item.id}`
            }
            href={withURL(item.link.final_url, { urlLangPrefix, product })}
            iconAnimation="navigational"
          >
            {item.link?.default_link_text || withSmartling('mktg_blog_read_more')}
          </Link>
        )}
      </Box>
    </Paper>
  );
};

export default InTextCta;
